import { areSourceIdentifiersShowOnStartUp } from '../../Coverage/areSourceIdentifiersShownOnStartUp';
import { loadFavoriteFilters } from './layerLoadingUtils';
import { shapeToVectorLayer } from '@ais-ui/react-openlayers';
import { isShapeLabelShown } from '../../../Shapes/isShapeShownOnStartUp';

interface LegacyLoadingDependencies {
  settings: any;
  sourceIdentifiers: any[];
  favoriteShapes: string[];
  enabledWmsLayers: any[];
  addSourceIdentifiersToMap: any;
  addLayer: any;
  addExistingLayerItem: any;
  doesLayerExist: (id: string) => boolean;
  shapeFetch: (id: string) => Promise<any>;
  onWmsToTileLayerWithLayerItem: any;
  removeShapeFromFavorites: (id: string) => void;
  toast: any;
  openLayersContext: {
    displayProjection: any;
    dataProjection: any;
  };
}

export const loadLegacyFavoriteLayers = async (
  deps: LegacyLoadingDependencies
): Promise<void> => {
  // Load coverage layers (below ships)
  await loadLegacyCoverageLayer(deps);

  // Load favorite shapes (below ships)
  await loadLegacyFavoriteShapes(deps);

  // Load enabled WMS layers (below ships by default)
  await loadLegacyWmsLayers(deps);
};

const loadLegacyCoverageLayer = async (
  deps: LegacyLoadingDependencies
): Promise<void> => {
  if (
    areSourceIdentifiersShowOnStartUp(deps.settings) &&
    deps.sourceIdentifiers.length > 0
  ) {
    deps.addSourceIdentifiersToMap(deps.addLayer, deps.sourceIdentifiers);
  }
};

const loadLegacyFavoriteShapes = async (
  deps: LegacyLoadingDependencies
): Promise<void> => {
  if (deps.favoriteShapes.length === 0) return;

  const promises = deps.favoriteShapes.map(async (shapeId: string) => {
    try {
      if (!deps.doesLayerExist(shapeId)) {
        const shape = await deps.shapeFetch(shapeId);
        const { displayProjection, dataProjection } = deps.openLayersContext;

        const layer = shapeToVectorLayer(
          shape,
          displayProjection,
          dataProjection
        );
        const name = shape?.properties?.name || 'No name';

        // Add type: 'shape' to properties for context menu detection
        const propertiesWithType = {
          ...shape.properties,
          type: 'shape',
        };

        deps.addLayer(
          layer,
          name,
          true,   // isRemovableFromLayerControl
          true,   // canZoomToFromLayerControl
          true,   // canShowLabel
          propertiesWithType,
          shape.properties.id,
          isShapeLabelShown(deps.settings, shapeId),
          false,  // clickable - no left-click behavior for shapes
          false,  // hoverEnabled
          'below-ships', // Default position for legacy
          true,   // contextMenuEnabled - enable right-click context menu
        );
      }
    } catch (error) {
      deps.removeShapeFromFavorites(shapeId);
      deps.toast({
        className: 'enable-shape-layer-error-toast',
        title: 'Error!',
        description: `Failed to enable layer ${shapeId}`,
        variant: 'destructive',
      });
    }
  });

  await Promise.allSettled(promises);
};

const loadLegacyWmsLayers = async (
  deps: LegacyLoadingDependencies
): Promise<void> => {
  for (const enabledLayer of deps.enabledWmsLayers) {
    try {
      if (!deps.doesLayerExist(enabledLayer.wmsLayerInfo.id)) {
        const { tileLayer, layerItem } =
          await deps.onWmsToTileLayerWithLayerItem(
            enabledLayer.wmsInfo,
            enabledLayer.wmsLayerInfo
          );

        if (!tileLayer || !layerItem) {
          deps.toast({
            className: 'enable-wms-layer-error-toast',
            title: 'Error!',
            description: `WMS layer ${enabledLayer.wmsLayerInfo.title} is invalid`,
            variant: 'destructive',
          });
          continue;
        }

        deps.addExistingLayerItem(tileLayer, layerItem, 'below-ships');
      }
    } catch (error) {
      deps.toast({
        className: 'enable-wms-layer-error-toast',
        title: 'Error!',
        description: `Failed to enable WMS layer ${enabledLayer.wmsLayerInfo.title}`,
        variant: 'destructive',
      });
    }
  }
};
